import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, StarIcon } from 'lucide-react';
import { plans } from '../data/plans';
import { usePlan } from '../contexts/PlanContext';
import { TrustRow } from './TrustRow';
type PlanSelectorProps = {
  groupName: string;
};
export function PlanSelector({
  groupName
}: PlanSelectorProps) {
  const {
    selected,
    setSelected
  } = usePlan();
  const current = plans.find(p => p.id === selected) ?? plans[0];
  return <div className="mx-auto w-full max-w-xl" data-id="element-174" data-mp="2qt626ow4ceyi">
      <div role="radiogroup" aria-label="Elegí tu Megapack" className="space-y-3" data-id="element-175" data-mp="2qsed7wx11fh7">
        {plans.map(p => {
        const isOn = p.id === selected;
        return <button key={p.id} type="button" role="radio" aria-checked={isOn} name={groupName} onClick={() => setSelected(p.id)} className={`relative flex w-full items-center gap-3 rounded-2xl px-4 text-left transition-[background-color,box-shadow,transform] duration-150 ease-out active:scale-[0.99] focus:outline-none focus-visible:ring-4 focus-visible:ring-pumpkin/40 sm:gap-4 sm:px-5 ${p.featured ? 'pb-3.5 pt-5' : 'py-3.5'} ${isOn ? p.featured ? 'bg-plum ring-2 ring-pumpkin' : 'bg-plum ring-2 ring-bone' : 'bg-dusk ring-1 ring-line hover:ring-mist/60'}`} data-id="element-176" data-mp="2zm9ayjvtk3mt">
              {p.featured && <span className="absolute -top-3 left-4 flex items-center gap-1 rounded-full bg-pumpkin px-2.5 py-0.5 text-xs font-extrabold text-night sm:left-5" data-id="element-177" data-mp="3f1eriyp9a96r">
                  <StarIcon className="h-3.5 w-3.5 fill-current" aria-hidden="true" data-id="element-178" data-mp="3nurwiz0lh197" />
                  MÁS ELEGIDO
                </span>}

              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 transition-colors duration-150 ease-out ${isOn ? p.featured ? 'ring-pumpkin' : 'ring-bone' : 'ring-mist/60'}`} aria-hidden="true" data-id="element-179" data-mp="1olfh2vpbdcu">
                {isOn && <span className={`h-2.5 w-2.5 rounded-full ${p.featured ? 'bg-pumpkin' : 'bg-bone'}`} data-id="element-180" data-mp="27iukpewr38g4" />}
              </span>

              <span className="min-w-0 flex-1" data-id="element-181" data-mp="22prpl2cbrqxt">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-182" data-mp="2ghy5qofh9xx9">
                  <span className="font-display text-lg font-bold leading-tight text-bone sm:text-xl" data-id="element-183" data-mp="2cgw4k2203bsn">{p.name}</span>
                  {p.savings && <span className="whitespace-nowrap rounded bg-slime px-1.5 py-0.5 text-[10px] font-extrabold text-night" data-id="element-184" data-mp="2qfcbnu3rz4bb">{p.savings}</span>}
                </span>
                <span className="mt-0.5 block text-xs text-mist sm:text-sm" data-id="element-185" data-mp="w3p55abitff3">{p.detail}</span>
                {p.extra && <span className="mt-0.5 block text-xs font-semibold text-pumpkin-soft" data-id="element-186" data-mp="r4kwcksidjpa">{p.extra}</span>}
              </span>

              <span className="shrink-0 text-right" data-id="element-187" data-mp="32roezwkz0eh3">
                <span className="block text-xs text-mist line-through sm:text-sm" data-id="element-188" data-mp="37qiv2qrjzoxi">{p.oldPrice}</span>
                <span className={`block font-display text-2xl font-bold leading-none sm:text-3xl ${p.featured ? 'text-pumpkin' : 'text-bone'}`} data-id="element-189" data-mp="2t1mo6x18ghyh">
                  {p.price}
                </span>
              </span>
            </button>;
      })}
      </div>

      <a href={current.checkoutUrl} className="group mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-pumpkin px-4 py-4 font-display text-[15px] font-bold text-night shadow-[0_6px_0_0_#b34d00] transition-[transform,box-shadow,background-color] duration-150 ease-out hover:bg-pumpkin-soft active:translate-y-1 active:shadow-[0_2px_0_0_#b34d00] focus:outline-none focus-visible:ring-4 focus-visible:ring-pumpkin/40 sm:text-lg" data-id="element-190" data-mp="2k2rh7iqd73u4">
        <AnimatePresence mode="wait" initial={false} data-id="element-191" data-mp="d7us0wz9melq">
          <motion.span key={current.id} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-192" data-mp="29c29kibvitam">
            {current.cta}
          </motion.span>
        </AnimatePresence>
        <ArrowRightIcon className="hidden h-5 w-5 transition-transform duration-150 ease-out group-hover:translate-x-0.5 sm:block" aria-hidden="true" data-id="element-193" data-mp="1vd2i72wz80ar" />
      </a>

      <p className="mt-3 text-center text-xs text-mist sm:text-sm" data-id="element-194" data-mp="1lmgcc0f2ao59">
        {current.featured ? 'La opción más elegida por familias que quieren una noche de Halloween legendaria' : 'Las dos opciones incluyen los 3 tomos extra completos'}
      </p>

      <div className="mt-4" data-id="element-195" data-mp="17ngnosrju993">
        <TrustRow data-id="element-196" data-mp="17moprbahbzfn" />
      </div>
    </div>;
}